import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type SectionType =
  | 'refractions'
  | 'internal'
  | 'external'
  | 'ccHpi'
  | 'other'
  | 'assessmentAndPlan';

export type ActiveRecordingStatus = 'recording' | 'paused' | 'stopped';
export type UploadStatus = 'pending' | 'uploading' | 'failed' | 'completed';

export interface ActiveRecording {
  sectionName: SectionType;
  status: ActiveRecordingStatus;
  startTime: number;
  pausedDuration: number;
  pauseStartTime: number | null;
}

export interface RecordedSection {
  id: string;
  sectionName: SectionType;
  startTime: number;
  duration: number;
  uploadStatus: UploadStatus;
  uploadAttempts: number;
  error: string | null;
}

export interface SectionRecordingState {
  activeRecording: ActiveRecording | null;
  recordedSections: RecordedSection[];
  appointmentId: string | null;
  patientId: string | null;
  locationId: string | null;
  scribeJobId: string | null;
}

const initialState: SectionRecordingState = {
  activeRecording: null,
  recordedSections: [],
  appointmentId: null,
  patientId: null,
  locationId: null,
  scribeJobId: null,
};

const sectionRecordingSlice = createSlice({
  name: 'sectionRecording',
  initialState,
  reducers: {
    // Set appointment context for section recordings
    setSectionRecordingContext: (
      state,
      action: PayloadAction<{
        appointmentId: string;
        patientId: string;
        locationId: string;
        scribeJobId: string;
      }>
    ) => {
      state.appointmentId = action.payload.appointmentId;
      state.patientId = action.payload.patientId;
      state.locationId = action.payload.locationId;
      state.scribeJobId = action.payload.scribeJobId;
    },

    // Start recording a new section
    // Only allowed if no active recording or active recording is stopped
    startSectionRecording: (state, action: PayloadAction<SectionType>) => {
      // Prevent starting if already recording or paused
      if (
        state.activeRecording &&
        (state.activeRecording.status === 'recording' ||
          state.activeRecording.status === 'paused')
      ) {
        console.warn('Cannot start recording: A recording is already active');
        return;
      }

      state.activeRecording = {
        sectionName: action.payload,
        status: 'recording',
        startTime: Date.now(),
        pausedDuration: 0,
        pauseStartTime: null,
      };
    },

    // Pause active recording
    pauseSectionRecording: (state) => {
      if (state.activeRecording?.status === 'recording') {
        state.activeRecording.status = 'paused';
        state.activeRecording.pauseStartTime = Date.now();
      }
    },

    // Resume paused recording
    resumeSectionRecording: (state) => {
      if (state.activeRecording?.status === 'paused') {
        const pauseStart = state.activeRecording.pauseStartTime;
        if (pauseStart) {
          const pausedTime = Date.now() - pauseStart;
          state.activeRecording.pausedDuration += pausedTime;
        }
        state.activeRecording.status = 'recording';
        state.activeRecording.pauseStartTime = null;
      }
    },

    // Stop recording and move to recorded sections
    stopSectionRecording: (state) => {
      if (!state.activeRecording) return;

      const duration =
        Date.now() -
        state.activeRecording.startTime -
        state.activeRecording.pausedDuration;

      // Create recorded section
      const recordedSection: RecordedSection = {
        id: `${state.activeRecording.sectionName}-${Date.now()}`,
        sectionName: state.activeRecording.sectionName,
        startTime: state.activeRecording.startTime,
        duration,
        uploadStatus: 'pending',
        uploadAttempts: 0,
        error: null,
      };

      state.recordedSections.push(recordedSection);
      state.activeRecording = null;
    },

    // Mark section as uploading
    markSectionUploading: (state, action: PayloadAction<string>) => {
      const section = state.recordedSections.find(
        (s) => s.id === action.payload
      );
      if (section) {
        section.uploadStatus = 'uploading';
        section.uploadAttempts += 1;
      }
    },

    // Mark section upload as completed (keep in recorded sections)
    completeSectionUpload: (state, action: PayloadAction<string>) => {
      const section = state.recordedSections.find(
        (s) => s.id === action.payload
      );
      if (section) {
        section.uploadStatus = 'completed';
        section.error = null;
      }
    },

    // Mark section upload as failed
    failSectionUpload: (
      state,
      action: PayloadAction<{ id: string; error: string }>
    ) => {
      const section = state.recordedSections.find(
        (s) => s.id === action.payload.id
      );
      if (section) {
        section.uploadStatus = 'failed';
        section.error = action.payload.error;
      }
    },

    // Retry failed upload
    retrySectionUpload: (state, action: PayloadAction<string>) => {
      const section = state.recordedSections.find(
        (s) => s.id === action.payload
      );
      if (section && section.uploadStatus === 'failed') {
        section.uploadStatus = 'pending';
        section.error = null;
      }
    },

    // Clear all recorded sections (useful for cleanup)
    clearRecordedSections: (state) => {
      state.recordedSections = [];
    },

    // Clear completed recordings for a specific section (to allow re-recording)
    clearCompletedSection: (state, action: PayloadAction<SectionType>) => {
      state.recordedSections = state.recordedSections.filter(
        (s) =>
          s.sectionName !== action.payload || s.uploadStatus !== 'completed'
      );
    },

    // Reset all section recordings
    resetAllSectionRecordings: (state) => {
      state.activeRecording = null;
      state.recordedSections = [];
    },

    // Clear section recording context
    clearSectionRecordingContext: (state) => {
      state.appointmentId = null;
      state.patientId = null;
      state.locationId = null;
      state.scribeJobId = null;
    },
  },
});

export const {
  setSectionRecordingContext,
  startSectionRecording,
  pauseSectionRecording,
  resumeSectionRecording,
  stopSectionRecording,
  markSectionUploading,
  completeSectionUpload,
  failSectionUpload,
  retrySectionUpload,
  clearRecordedSections,
  clearCompletedSection,
  resetAllSectionRecordings,
  clearSectionRecordingContext,
} = sectionRecordingSlice.actions;

export default sectionRecordingSlice.reducer;
